import React, { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { ConfirmModal, Toast } from "./Feedback";
import { api, clearAuth, getStoredAuth } from "../api/client";

export default function MainLayout({ user, setUser }) {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [collections, setCollections] = useState([]);
  
  // State khusus chat
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Voice state — lifted here so it persists across navigation
  const [voiceSessionId, setVoiceSessionId] = useState(null);
  const [voiceTurns, setVoiceTurns] = useState([]);
  const voiceSessionIdRef = useRef(null); // ref avoids stale closure in async callbacks

  const navigate = useNavigate();

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => setToast(null), 4500);
  }, []);

  const loadSessions = useCallback(async () => {
    const rows = await api.chatSessions();
    setSessions(rows);
    if (!activeSessionId && rows[0]?.id) setActiveSessionId(rows[0].id);
  }, [activeSessionId]);

  const loadCollections = useCallback(async () => {
    setCollections(await api.collections());
  }, []);

  useEffect(() => {
    loadSessions().catch((err) => showToast(err.message || "Gagal memuat session", "error"));
    loadCollections().catch((err) => showToast(err.message || "Gagal memuat collection", "error"));
  }, [loadSessions, loadCollections, showToast]);

  useEffect(() => {
    if (!activeSessionId) {
      setMessages([]);
      return;
    }
    api.chatMessages(activeSessionId).then(setMessages).catch((err) => showToast(err.message || "Gagal memuat pesan", "error"));
  }, [activeSessionId, showToast]);

  async function createSession() {
    try {
      const result = await api.createChatSession({ title: null, channel: "web" });
      await loadSessions();
      setActiveSessionId(result.id);
      navigate("/chat");
      showToast("Chat baru berhasil dibuat.");
    } catch (err) {
      showToast(err.message || "Gagal membuat chat baru", "error");
    }
  }

  async function openSession(sessionId) {
    setActiveSessionId(sessionId);
    navigate("/chat");
  }

  // Reset voice session whenever the active chat session changes
  useEffect(() => {
    setVoiceSessionId(null);
    voiceSessionIdRef.current = null;
    setVoiceTurns([]);
  }, [activeSessionId]);

  async function archiveSession(sessionId) {
    await api.archiveChatSession(sessionId);
    if (activeSessionId === sessionId) setActiveSessionId(null);
    await loadSessions();
  }

  // ── Voice helpers ────────────────────────────────────────────────────────────

  async function ensureVoiceSession() {
    if (voiceSessionIdRef.current) return voiceSessionIdRef.current;
    let chatId = activeSessionId;
    if (!chatId) {
      const result = await api.createChatSession({ title: null, channel: "voice" });
      chatId = result.id;
      setActiveSessionId(chatId);
      api.chatSessions().then(setSessions).catch(() => {});
    }
    const res = await api.createVoiceSession({ chat_session_id: chatId });
    voiceSessionIdRef.current = res.id;
    setVoiceSessionId(res.id);
    return res.id;
  }

  async function sendVoiceTurnAction(formData) {
    const vsid = await ensureVoiceSession();
    const result = await api.sendVoiceTurn(vsid, formData);
    setVoiceTurns((prev) => [...prev, result]);
    // Refresh chat messages so ChatView also reflects the voice turn as text
    const chatId = result.chat_session_id || activeSessionId;
    if (chatId) {
      api.chatMessages(chatId).then(setMessages).catch(() => {});
      api.chatSessions().then(setSessions).catch(() => {});
    }
    return result;
  }

  function resetVoiceSession() {
    setVoiceSessionId(null);
    voiceSessionIdRef.current = null;
    setVoiceTurns([]);
  }

  function requestArchiveSession(session) {
    setConfirmAction({
      title: "Archive chat?",
      message: `Chat "${session.title || "Chat baru"}" akan dipindahkan dari daftar recent chats. Pesannya tidak dihapus permanen.`,
      confirmText: "Archive",
      danger: false,
      run: async () => {
        await api.archiveChatSession(session.id);
        if (activeSessionId === session.id) setActiveSessionId(null);
        await loadSessions();
        showToast("Chat berhasil diarsipkan.");
      },
    });
  }

  function requestDeleteSession(session) {
    setConfirmAction({
      title: "Delete chat?",
      message: `Chat "${session.title || "Chat baru"}" akan dihapus permanen beserta pesannya. Aksi ini tidak bisa dibatalkan.`,
      confirmText: "Delete",
      danger: true,
      run: async () => {
        await api.deleteChatSession(session.id);
        if (activeSessionId === session.id) setActiveSessionId(null);
        await loadSessions();
        showToast("Chat berhasil dihapus permanen.");
      },
    });
  }

  async function confirmCurrentAction() {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      await confirmAction.run();
      setConfirmAction(null);
    } catch (err) {
      showToast(err.message || "Aksi gagal diproses", "error");
    } finally {
      setConfirmLoading(false);
    }
  }

  async function sendMessage(body) {
    if (!activeSessionId) return;
    const userMessage = { id: `tmp-${Date.now()}`, role: "user", content: body.message, created_at: new Date().toISOString() };
    setMessages((rows) => [...rows, userMessage]);
    setSending(true);
    try {
      await api.sendChatMessage(activeSessionId, body);
      const rows = await api.chatMessages(activeSessionId);
      setMessages(rows);
      await loadSessions();
      showToast("Pesan berhasil dikirim.");
    } catch (err) {
      showToast(err.message || "Gagal mengirim pesan", "error");
      throw err;
    } finally {
      setSending(false);
    }
  }

  async function logout() {
    const { refreshToken } = getStoredAuth();
    try {
      if (refreshToken) await api.logout(refreshToken);
    } catch (_) {}
    clearAuth();
    setUser(null);
  }

  const contextValue = {
    user,
    setUser,
    sessions,
    activeSessionId,
    collections,
    loadCollections,
    messages,
    sendMessage,
    sending,
    createSession,
    showToast,
    voiceSessionId,
    voiceTurns,
    sendVoiceTurnAction,
    resetVoiceSession,
  };

  return (
    <div className="bg-white text-gray-900 flex h-screen overflow-hidden w-full">
      <Sidebar
        user={user}
        sessions={sessions}
        activeSessionId={activeSessionId}
        openSession={openSession}
        createSession={createSession}
        archiveSession={archiveSession}
        requestArchiveSession={requestArchiveSession}
        requestDeleteSession={requestDeleteSession}
        logout={logout}
      />
      {/* Content area — flex-1 naturally takes remaining width beside the static sidebar */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Outlet context={contextValue} />
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
      <ConfirmModal
        open={Boolean(confirmAction)}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmText={confirmAction?.confirmText}
        danger={confirmAction?.danger}
        loading={confirmLoading}
        onConfirm={confirmCurrentAction}
        onCancel={() => !confirmLoading && setConfirmAction(null)}
      />
    </div>
  );
}
